import axios from 'axios';
import * as Sentry from '@sentry/react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
const SESSION_NOTICE_STORAGE_KEY = 'pinecurl.session.notice';
const UUID_SEGMENT_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const HEX_SEGMENT_REGEX = /^0x[a-f0-9]{8,}$/i;
const LONG_TOKEN_SEGMENT_REGEX = /^[A-Za-z0-9_-]{16,}$/;

export type AuthRefreshStatus = 'idle' | 'retrying' | 'degraded' | 'sign_in_required';

export interface AuthRefreshState {
  status: AuthRefreshStatus;
  message: string | null;
  code?: string;
  retryable?: boolean;
  nextAction?: string;
  sessionState?: string;
  updatedAt: number;
}

interface ApiErrorMetadata {
  status?: number;
  code?: string;
  retryable: boolean;
  degraded: boolean;
  nextAction?: string;
  sessionState?: string;
  message?: string;
  upstream?: string;
}

const DEFAULT_AUTH_REFRESH_STATE: AuthRefreshState = {
  status: 'idle',
  message: null,
  updatedAt: 0,
};

let authRefreshState: AuthRefreshState = DEFAULT_AUTH_REFRESH_STATE;
let refreshRequestPromise: Promise<string> | null = null;
const authRefreshListeners = new Set<(state: AuthRefreshState) => void>();

function emitAuthRefreshState(nextState: AuthRefreshState) {
  authRefreshState = nextState;
  authRefreshListeners.forEach((listener) => listener(authRefreshState));
}

function setAuthRefreshState(
  state: Omit<AuthRefreshState, 'updatedAt'> & { updatedAt?: number }
) {
  emitAuthRefreshState({
    ...DEFAULT_AUTH_REFRESH_STATE,
    ...state,
    updatedAt: state.updatedAt ?? Date.now(),
  });
}

function persistSessionNotice(message: string) {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(SESSION_NOTICE_STORAGE_KEY, message);
}

export function getStoredSessionNotice(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.sessionStorage.getItem(SESSION_NOTICE_STORAGE_KEY);
}

export function clearStoredSessionNotice() {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.removeItem(SESSION_NOTICE_STORAGE_KEY);
}

export function getAuthRefreshState(): AuthRefreshState {
  return authRefreshState;
}

export function clearAuthRefreshState() {
  setAuthRefreshState(DEFAULT_AUTH_REFRESH_STATE);
}

export function subscribeAuthRefreshState(
  listener: (state: AuthRefreshState) => void
) {
  authRefreshListeners.add(listener);
  listener(authRefreshState);

  return () => {
    authRefreshListeners.delete(listener);
  };
}

function getApiErrorMetadata(error: {
  response?: { status?: number; data?: Record<string, unknown> };
  message?: string;
}): ApiErrorMetadata {
  const responseData = error.response?.data ?? {};

  return {
    status: error.response?.status,
    code: typeof responseData.code === 'string' ? responseData.code : undefined,
    retryable: responseData.retryable === true,
    degraded: responseData.degraded === true,
    nextAction:
      typeof responseData.nextAction === 'string' ? responseData.nextAction : undefined,
    sessionState:
      typeof responseData.sessionState === 'string'
        ? responseData.sessionState
        : undefined,
    upstream:
      typeof responseData.upstream === 'string' ? responseData.upstream : undefined,
    message:
      typeof responseData.message === 'string'
        ? responseData.message
        : typeof responseData.error === 'string'
          ? responseData.error
          : error.message,
  };
}

function isRetryableRefreshFailure(
  metadata: ApiErrorMetadata,
  error: { response?: unknown }
): boolean {
  if (!error.response) {
    return true;
  }

  if (metadata.retryable || metadata.degraded) {
    return true;
  }

  if (metadata.nextAction === 'retry' || metadata.sessionState === 'degraded') {
    return true;
  }

  return (
    metadata.code?.includes('timeout') === true ||
    metadata.code?.includes('unavailable') === true
  );
}

function getDegradedSessionMessage(metadata: ApiErrorMetadata): string {
  if (metadata.upstream === 'auth-service') {
    return 'Session validation is delayed while the auth service recovers. Some actions may need another try.';
  }

  return 'Session validation is delayed. You can keep browsing, but some actions may need another try.';
}

function getSignInRequiredMessage(metadata: ApiErrorMetadata): string {
  if (metadata.sessionState === 'expired' || metadata.code === 'refresh_token_expired') {
    return 'Session expired. Please sign in again.';
  }

  if (metadata.code === 'refresh_token_revoked') {
    return 'Your session was revoked. Please sign in again.';
  }

  if (metadata.code === 'refresh_token_invalid') {
    return 'Your session is no longer valid. Please sign in again.';
  }

  return 'We could not restore your session. Please sign in again.';
}

async function refreshSessionToken(force = false): Promise<string> {
  if (refreshRequestPromise && !force) {
    return refreshRequestPromise;
  }

  const refreshToken = localStorage.getItem('refreshToken');

  if (!refreshToken) {
    const message = 'Session expired. Please sign in again.';
    localStorage.removeItem('accessToken');
    persistSessionNotice(message);
    setAuthRefreshState({
      status: 'sign_in_required',
      message,
      nextAction: 'sign_in',
    });
    throw new Error(message);
  }

  setAuthRefreshState({
    status: 'retrying',
    message: 'Reconnecting your session…',
  });

  refreshRequestPromise = (async () => {
    try {
      const response = await axios.post<{ accessToken?: string }>(
        `${API_BASE_URL}/auth/refresh`,
        { refreshToken },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.data?.accessToken) {
        throw new Error('Refresh response did not include a new access token.');
      }

      localStorage.setItem('accessToken', response.data.accessToken);
      clearStoredSessionNotice();
      clearAuthRefreshState();

      return response.data.accessToken;
    } catch (error) {
      const metadata = getApiErrorMetadata(
        error as { response?: { status?: number; data?: Record<string, unknown> }; message?: string }
      );

      if (isRetryableRefreshFailure(metadata, error as { response?: unknown })) {
        setAuthRefreshState({
          status: 'degraded',
          message: getDegradedSessionMessage(metadata),
          code: metadata.code,
          retryable: true,
          nextAction: metadata.nextAction,
          sessionState: metadata.sessionState,
        });
      } else {
        const message = getSignInRequiredMessage(metadata);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        persistSessionNotice(message);
        setAuthRefreshState({
          status: 'sign_in_required',
          message,
          code: metadata.code,
          retryable: false,
          nextAction: metadata.nextAction ?? 'sign_in',
          sessionState: metadata.sessionState,
        });
      }

      throw error;
    } finally {
      refreshRequestPromise = null;
    }
  })();

  return refreshRequestPromise;
}

export async function retrySessionRefresh(): Promise<boolean> {
  try {
    await refreshSessionToken(true);
    return true;
  } catch {
    return false;
  }
}

function sanitizePathSegment(segment: string): string {
  if (!segment) {
    return segment;
  }

  if (/^\d+$/.test(segment) || UUID_SEGMENT_REGEX.test(segment)) {
    return ':id';
  }

  if (HEX_SEGMENT_REGEX.test(segment) || LONG_TOKEN_SEGMENT_REGEX.test(segment)) {
    return ':token';
  }

  return segment;
}

function sanitizeApiPath(url?: string): string {
  if (!url) {
    return 'unknown';
  }

  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const sanitizedPath = parsed.pathname
      .split('/')
      .map(sanitizePathSegment)
      .join('/');

    return sanitizedPath.startsWith('/') ? sanitizedPath : `/${sanitizedPath}`;
  } catch {
    return url.split('?')[0] || 'unknown';
  }
}

function isAbortedRequest(error: { code?: string; message?: string; name?: string }): boolean {
  const normalizedMessage = error.message?.trim().toLowerCase();

  return (
    normalizedMessage === 'request aborted' ||
    normalizedMessage === 'canceled' ||
    normalizedMessage === 'cancelled' ||
    normalizedMessage === 'the operation was aborted.' ||
    normalizedMessage === 'the user aborted a request.' ||
    error.code === 'ERR_CANCELED' ||
    error.name === 'CanceledError' ||
    error.name === 'AbortError'
  );
}

function isNetworkTransportFailure(error: {
  code?: string;
  message?: string;
  name?: string;
}): boolean {
  const normalizedMessage = error.message?.trim().toLowerCase();

  return (
    normalizedMessage === 'network error' ||
    normalizedMessage === 'failed to fetch' ||
    error.code === 'ERR_NETWORK'
  );
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,   // send/receive httpOnly cookies cross-origin
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url ?? '';

    // If 403 and not already retried, try to refresh token
    if (
      error.response?.status === 403 &&
      !originalRequest?._retry &&
      !requestUrl.includes('/auth/refresh')
    ) {
      originalRequest._retry = true;

      try {
        const accessToken = await refreshSessionToken();
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    // Preserve handled HTTP failures as sanitized breadcrumbs so upstream 4xx/5xx
    // responses stay visible in traces/replays without creating frontend issues.
    const status = error.response?.status;
    const method = error.config?.method?.toUpperCase() ?? 'UNKNOWN';
    const path = sanitizeApiPath(error.config?.url);

    if (status != null && status !== 401) {
      Sentry.addBreadcrumb({
        category: 'api',
        type: 'http',
        level: status >= 500 ? 'warning' : 'info',
        message: `${method} ${path} -> ${status}`,
        data: {
          method,
          path,
          status,
        },
      });
    } else if (status == null && isAbortedRequest(error)) {
      Sentry.addBreadcrumb({
        category: 'api',
        type: 'http',
        level: 'info',
        message: `${method} ${path} -> aborted`,
        data: {
          method,
          path,
          status: 'aborted',
        },
      });
    } else if (status == null && isNetworkTransportFailure(error)) {
      Sentry.addBreadcrumb({
        category: 'api',
        type: 'http',
        level: 'warning',
        message: `${method} ${path} -> network_error`,
        data: {
          method,
          path,
          status: 'network_error',
          code: error.code ?? 'ERR_NETWORK',
        },
      });
    } else if (status == null) {
      Sentry.withScope((scope) => {
        scope.setTag('api.method', method);
        scope.setTag('api.path', path);
        scope.setTag('api.status', 'network_error');
        if (error.code) {
          scope.setTag('api.code', error.code);
        }
        scope.setContext('api_error', {
          method,
          path,
          code: error.code,
          message: error.message,
        });
        Sentry.captureException(error);
      });
    }

    return Promise.reject(error);
  }
);

export default apiClient;
